import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Card,
  Stack,
  Paper,
  Button,
  CardProps,
  Typography,
  CardHeader,
  CardContent,
  FormHelperText,
  FormControlLabel,
  FormGroup,
  Checkbox,
} from '@mui/material';
import { Iconic } from '@/commons/iconic';
import { currentFormat } from '@/utils/cases';
import { translates } from '@/utils';
import PaymentNewCardDialog from './PaymentNewCardDialog';

interface Props extends CardProps {
  paymentOptions: any[];
  cardOptions: any[];
}

export default function CheckoutPaymentMethods({ paymentOptions, cardOptions, ...other }: Props) {
  const { control } = useFormContext();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const handleOpen = (nameTemp: string) => {
    setOpen(true);
    setName(nameTemp);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Card {...other}>
        <CardHeader title={translates.paymentMethod} />

        <CardContent>
          <Stack spacing={3} sx={{ width: 1 }}>
            {paymentOptions.map((option, index) => {
              const isOption = true;
              return (
                <Controller
                  key={index}
                  name={`${option.value}.status`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup row {...field}>
                      <PaymentOption
                        key={option.title}
                        option={option}
                        isSelected={field.value === option.value}
                        hasChild={isOption}
                        onOpen={handleOpen}
                      />

                      {!!error && (
                        <FormHelperText error sx={{ pt: 1, px: 2 }}>
                          {error.message}
                        </FormHelperText>
                      )}
                    </FormGroup>
                  )}
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <PaymentNewCardDialog {...{ open, name }} onClose={handleClose} />
    </>
  );
}

type PaymentOptionProps = {
  option: any;
  hasChild: boolean;
  isSelected: boolean;
  onOpen: (name: string) => void;
};

function PaymentOption({ option, hasChild, isSelected, onOpen }: PaymentOptionProps) {
  const { value, title, description } = option;
  const { getValues, setValue } = useFormContext();
  const values = getValues();

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        transition: (theme) => theme.transitions.create('all'),
        ...(isSelected && {
          boxShadow: (theme) => theme.customShadows.z20,
        }),
        ...(hasChild && {
          flexWrap: 'wrap',
        }),
      }}
    >
      <FormControlLabel
        value={value}
        control={
          <Checkbox
            onChange={(_, checked) => setValue(value, { status: checked })}
            checkedIcon={<Iconic icon="eva:checkmark-circle-2-fill" />}
          />
        }
        label={
          <Box sx={{ ml: 1 }}>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {description}
            </Typography>
          </Box>
        }
        sx={{ py: 3, pl: 2.5, mr: 0, flexGrow: 1 }}
      />

      {values[value]?.status && (
        <Stack
          alignItems="flex-start"
          sx={{
            px: 3,
            width: 1,
          }}
        >
          {values[value]?.value && (
            <Typography variant="h5" sx={{ color: 'text.secondary' }}>
              {translates.valueTransaction}: {currentFormat(values[value]?.value)}
            </Typography>
          )}
          {values[value]?.transaction && (
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {translates.numberTransaction}: {values[value]?.transaction}
            </Typography>
          )}

          <Button
            size="small"
            startIcon={<Iconic icon="eva:plus-fill" />}
            onClick={() => onOpen(value ?? '')}
            sx={{ my: 3, textTransform: 'unset' }}
          >
            {values[value]?.transaction
              ? [translates.editValueToCancel]
              : [translates.addValueToCancel]}
          </Button>
        </Stack>
      )}
    </Paper>
  );
}
